/* eslint-disable */
import axios from 'axios'
import env from '../env/env'
import { getAccessToken } from '../utils/AuthUtil'
import { logErrorToServer } from '../utils/errorLogger'
import PostHogTracker from '../utils/PostHogTracker'

const shouldSkip401Redirect = (url) => {
  let requestPath = url

  try {
    requestPath = new URL(url, window.location.origin).pathname + new URL(url, window.location.origin).search
  } catch (error) {
    // Keep the original URL for non-browser or malformed URL callers.
  }

  return env.DO_NOT_VALIDATE_401.some(
    (endpoint) => requestPath === endpoint || requestPath.startsWith(`${endpoint}?`),
  )
}

const client = axios.create({
  baseURL: env.API_URL,
})

const isFormDataPayload = (data) =>
  typeof FormData !== 'undefined' && data instanceof FormData

const getSafeRequestPayload = (data) => {
  if (data == null || data === '') return null
  if (isFormDataPayload(data)) return '[FormData]'
  if (typeof data === 'string') {
    try {
      return JSON.parse(data)
    } catch {
      return data
    }
  }
  return data
}

const setMultipartContentType = (headers) => {
  if (!headers) return
  if (typeof headers.setContentType === 'function') {
    headers.setContentType('multipart/form-data')
    return
  }
  headers['Content-Type'] = 'multipart/form-data'
}

client.interceptors.response.use(
  (response) => response,
  (error) => {
    // 🛑 Skip interceptor if this is the logging request itself
    if (error.config?.headers?.['x-no-error-log']) {
      return Promise.reject(error)
    }

    const log = {
      type: 'api-error',
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      stack: error.stack,
      payload: getSafeRequestPayload(error.config?.data),
      response: error.response?.data || null,
      time: new Date().toISOString(),
    }
    console.error('Axios API Error:', log)

    // Track API errors with PostHog
    const endpoint = error.config?.url || 'unknown'
    const statusCode = error.response?.status
    const errorMessage = error.response?.data?.message || error.message || 'Unknown error'
    const errorCode = error.response?.data?.errorCode || error.code || 'unknown'

    // Track different error types
    if (statusCode === 401 || statusCode === 403) {
      PostHogTracker.tokenExpiredError(endpoint)
    } else if (!error.response) {
      // Network error
      PostHogTracker.networkError(endpoint, errorMessage)
    } else {
      // General API error
      PostHogTracker.apiError(endpoint, errorCode, errorMessage, statusCode)
    }

    // logErrorToServer(log)
    return Promise.reject(error)
  },
)

const getConfig = async (headers, params) => {
  let config = {}
  if (!headers) {
    headers = {}
  }
  const jwtToken = await getAccessToken()
  if (jwtToken) {
    headers.Authorization = 'Bearer ' + jwtToken
  }
  config = {
    headers,
  }

  if (params) {
    config.params = params
  }
  return config
}

const getErrorPayload = (e) => ({
  isError: true,
  status: e.response?.status,
  response: e.response?.data,
  message: e.response?.data?.message || e.message || 'Unable to process your request',
})

const post = async (url, data, headers, params) => {
  try {
    const config = await getConfig(headers, params)
    if (isFormDataPayload(data)) {
      setMultipartContentType(config.headers)
      config.adapter = 'fetch'
    }
    const response = await client.post(url, data, config)
    return response.data
  } catch (e) {
    const status = e.response?.status
    if (
      (status === 401 || status === 403) &&
      !shouldSkip401Redirect(url)
    ) {
      window.goToLogin()
    }
    return getErrorPayload(e)
  }
}

const postRequest = async (url, data, headers, params) => {
  try {
    const config = await getConfig(headers, params)
    if (isFormDataPayload(data)) {
      setMultipartContentType(config.headers)
      config.adapter = 'fetch'
    }
    const response = await client.post(url, data, config)
    return response
  } catch (e) {
    const status = e.response?.status
    if (
      (status === 401 || status === 403) &&
      !shouldSkip401Redirect(url)
    ) {
      window.goToLogin()
    }
    return getErrorPayload(e)
  }
}

const patch = async (url, data, headers, params) => {
  try {
    const config = await getConfig(headers, params)
    const response = await client.patch(url, data, config)
    return response.data
  } catch (e) {
    const status = e.response?.status
    if (
      (status === 401 || status === 403) &&
      !shouldSkip401Redirect(url)
    ) {
      window.goToLogin()
    }
    return getErrorPayload(e)
  }
}

const get = async (url, headers, params) => {
  try {
    const config = await getConfig(headers, params)
    const response = await client.get(url, config)
    return response.data
  } catch (e) {
    const status = e.response?.status
    if ((status === 401 || status === 403) && !shouldSkip401Redirect(url)) {
      window.goToLogin()
    }
    return getErrorPayload(e)
  }
}

const getMFData = async (headers, params) => {
  const url = '/mutualfundservice/api/mfData/allFunds'
  try {
    const config = await getConfig(headers, params)
    const response = await client.get(url, config)
    return response.data
  } catch (e) {
    const status = e.response?.status
    if (
      (status === 401 || status === 403) &&
      !shouldSkip401Redirect(url)
    ) {
      window.goToLogin()
    }
    return getErrorPayload(e)
  }
}

const deleteReq = async (url, headers, params) => {
  try {
    const config = await getConfig(headers, params)
    const response = await client.delete(url, config)
    return response
  } catch (e) {
    const status = e.response?.status
    if (
      (status === 401 || status === 403) &&
      !shouldSkip401Redirect(url)
    ) {
      window.goToLogin()
    }
    return getErrorPayload(e)
  }
}

export { post, get, patch, postRequest, deleteReq, getConfig, getMFData }
